/**
 * Client store for the Menu screen.
 *
 * Holds the item list plus the list's UI state (search text, page, page size).
 * The rows come from `menu/items/` through `getMenuItemsSSR` and are hydrated
 * here by `MenuInitializer` — the slice itself never fetches.
 *
 * Search, filters and paging live here as UI state; `MenuQuerySync` mirrors them
 * into the URL, which is what actually drives the server read. Nothing in this
 * file filters — the API returns the page already narrowed.
 */
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type {
  ModifierGroup,
  ModifierOption,
} from "@/components/menu/modifiers/types";
import type { MenuItem, MenuItemPage } from "@/components/menu/types";

/**
 * Which overlay is on screen. Only one can be open at a time, so this is a
 * single discriminator rather than a boolean per drawer.
 */
export type MenuOverlay =
  | "add"
  | "edit"
  | "details"
  | "delete"
  | "modifiers"
  | "draft";

/** Filter panel selections (Figma section 2457:52590). */
export interface MenuFilters {
  courses: string[];
  flags: string[];
  kdsRouting: string[];
  ingredients: string[];
}

/**
 * The list state the URL carries — what `MenuInitializer` adopts on mount.
 *
 * Mirrors the search params `MenuQuerySync` writes, so the two halves of the
 * round trip cannot drift.
 */
export interface MenuListQuery {
  search: string;
  filters: MenuFilters;
  page: number;
  pageSize: number;
}

export const EMPTY_FILTERS: MenuFilters = {
  courses: [],
  flags: [],
  kdsRouting: [],
  ingredients: [],
};

export interface MenuState {
  items: MenuItem[];
  /** Total rows across all pages — drives the "1-10 of 180" label. */
  total: number;
  page: number;
  pageSize: number;
  search: string;
  /** Open overlay, or null when the list is uncovered. */
  overlay: MenuOverlay | null;
  /** Row the open overlay acts on — null for "add" and "draft". */
  activeItemId: string | null;
  filters: MenuFilters;
  /**
   * Blob object-URLs for the photos picked in the add/edit form — the design
   * allows up to MENU_PHOTO_MAX, shown as a card grid. Held here (not in
   * component state) so the grid, the overlay actions and the lightbox all read
   * one list. Always revoke a URL before dropping it.
   */
  photos: string[];
  /** Index into `photos` used as the cover image (the ticked card). */
  coverIndex: number;
  /** Index currently open in the full-screen lightbox, or null when closed. */
  lightboxIndex: number | null;
  /**
   * Modifier groups shown in the Add Modifiers drawer, seeded from the tenant's
   * catalogue (`menu/modifier-groups/`, which embeds each group's options) when
   * the drawer opens. Empty until then — there is no design-time seed, because
   * a seeded group carries an id the API has never heard of.
   */
  modifierGroups: ModifierGroup[];
  /**
   * Items saved via "Save Draft" — unpublished, listed in the Menu Draft
   * drawer. Same shape as a real item so a draft can be promoted as-is.
   */
  drafts: MenuItem[];
  /** A list navigation is in flight — see `setMenuListPending`. */
  listPending: boolean;
}

/** Maximum photos per item — Figma's "(Image n/5)" counter. */
export const MENU_PHOTO_MAX = 5;

const initialState: MenuState = {
  items: [],
  total: 0,
  page: 1,
  pageSize: 20,
  search: "",
  overlay: null,
  activeItemId: null,
  filters: EMPTY_FILTERS,
  photos: [],
  coverIndex: 0,
  lightboxIndex: null,
  modifierGroups: [],
  drafts: [],
  listPending: false,
};

const menuSlice = createSlice({
  name: "menu",
  initialState,
  reducers: {
    /** Replace the list with a server-rendered page (called by MenuInitializer). */
    setMenuItems(state, action: PayloadAction<MenuItemPage>) {
      state.items = action.payload.items;
      state.total = action.payload.total;
      state.page = action.payload.page;
      state.pageSize = action.payload.pageSize;
    },
    /**
     * Adopt the URL's list state verbatim, on mount.
     *
     * **Not the individual setters**, which reset `page` to 1 — right when a
     * user changes a facet, wrong here: it would drop anyone landing on
     * `?page=3&course=<uuid>` back to the first page.
     *
     * Without this the URL is write-only: `MenuQuerySync` rebuilds the query
     * from this slice, so a fresh store — a reload, or a link someone shared —
     * wipes the params the page was just rendered with.
     */
    setMenuListQuery(state, action: PayloadAction<MenuListQuery>) {
      state.search = action.payload.search;
      state.filters = action.payload.filters;
      state.page = action.payload.page;
      state.pageSize = action.payload.pageSize;
    },
    setMenuPage(state, action: PayloadAction<number>) {
      state.page = action.payload;
    },
    setMenuPageSize(state, action: PayloadAction<number>) {
      state.pageSize = action.payload;
      // A larger page size can put the current page out of range
      state.page = 1;
    },
    setMenuSearch(state, action: PayloadAction<string>) {
      state.search = action.payload;
      // Any new query restarts paging from the first page
      state.page = 1;
    },
    /**
     * True while a list navigation (search, filter, page) is in flight.
     *
     * Written by `MenuQuerySync` from its `useTransition`. Without it the table
     * sits perfectly still through the whole server round trip, which reads as
     * a frozen screen rather than a loading one.
     */
    setMenuListPending(state, action: PayloadAction<boolean>) {
      state.listPending = action.payload;
    },
    /** Optimistic removal until the delete endpoint exists. */
    removeMenuItem(state, action: PayloadAction<string>) {
      state.items = state.items.filter((item) => item.id !== action.payload);
      state.total = Math.max(0, state.total - 1);
    },

    /** Open an overlay. `itemId` is required for row-scoped overlays. */
    openMenuOverlay(
      state,
      action: PayloadAction<{ overlay: MenuOverlay; itemId?: string }>,
    ) {
      state.overlay = action.payload.overlay;
      state.activeItemId = action.payload.itemId ?? null;
    },
    closeMenuOverlay(state) {
      state.overlay = null;
      state.activeItemId = null;
    },

    setMenuFilters(state, action: PayloadAction<MenuFilters>) {
      state.filters = action.payload;
      state.page = 1; // a new filter set restarts paging
    },
    clearMenuFilters(state) {
      state.filters = EMPTY_FILTERS;
      state.page = 1;
    },

    /** Append newly picked object-URLs, never exceeding MENU_PHOTO_MAX. */
    addMenuPhotos(state, action: PayloadAction<string[]>) {
      const room = MENU_PHOTO_MAX - state.photos.length;
      if (room > 0) state.photos.push(...action.payload.slice(0, room));
    },
    /**
     * Drop one photo by index. The caller must revoke its URL first — a reducer
     * cannot, since that is a side effect.
     */
    removeMenuPhoto(state, action: PayloadAction<number>) {
      state.photos.splice(action.payload, 1);
      // Keep the cover pointing at a real card
      if (state.coverIndex >= state.photos.length) {
        state.coverIndex = Math.max(0, state.photos.length - 1);
      }
      state.lightboxIndex = null;
    },
    setMenuCoverPhoto(state, action: PayloadAction<number>) {
      state.coverIndex = action.payload;
    },
    setMenuLightboxIndex(state, action: PayloadAction<number | null>) {
      state.lightboxIndex = action.payload;
    },

    // ─── Add Modifiers drawer (Figma 2454:125319) ───────────────────────────
    /**
     * Replace the groups with the tenant's catalogue.
     *
     * Called when the drawer opens. Any `selected` marks already applied to the
     * dish are re-applied by the caller before dispatching, so this is a plain
     * replace rather than a merge.
     */
    setModifierGroups(state, action: PayloadAction<ModifierGroup[]>) {
      state.modifierGroups = action.payload;
    },
    /** Append a group chosen in the "+ Add Modifier" picker. */
    addModifierGroup(state, action: PayloadAction<ModifierGroup>) {
      if (!state.modifierGroups.some((g) => g.id === action.payload.id)) {
        state.modifierGroups.push(action.payload);
      }
    },
    removeModifierGroup(state, action: PayloadAction<string>) {
      state.modifierGroups = state.modifierGroups.filter(
        (g) => g.id !== action.payload,
      );
    },
    /** Required / Multi-Select checkboxes in a group's footer row. */
    setModifierGroupFlag(
      state,
      action: PayloadAction<{
        groupId: string;
        flag: "required" | "multiSelect";
        value: boolean;
      }>,
    ) {
      const group = state.modifierGroups.find(
        (g) => g.id === action.payload.groupId,
      );
      if (group) group[action.payload.flag] = action.payload.value;
    },
    /** Add an option through the "+ Add options" popover. */
    addModifierOption(
      state,
      action: PayloadAction<{ groupId: string; option: ModifierOption }>,
    ) {
      const group = state.modifierGroups.find(
        (g) => g.id === action.payload.groupId,
      );
      // Ignore duplicates — the popover lists options that may already be added
      if (
        group &&
        !group.options.some((o) => o.label === action.payload.option.label)
      ) {
        group.options.push(action.payload.option);
      }
    },
    removeModifierOption(
      state,
      action: PayloadAction<{ groupId: string; optionId: string }>,
    ) {
      const group = state.modifierGroups.find(
        (g) => g.id === action.payload.groupId,
      );
      if (group) {
        group.options = group.options.filter(
          (o) => o.id !== action.payload.optionId,
        );
      }
    },
    toggleModifierOption(
      state,
      action: PayloadAction<{ groupId: string; optionId: string }>,
    ) {
      const option = state.modifierGroups
        .find((g) => g.id === action.payload.groupId)
        ?.options.find((o) => o.id === action.payload.optionId);
      if (option) option.selected = !option.selected;
    },
    setModifierOptionPrice(
      state,
      action: PayloadAction<{
        groupId: string;
        optionId: string;
        priceDelta: number;
      }>,
    ) {
      const option = state.modifierGroups
        .find((g) => g.id === action.payload.groupId)
        ?.options.find((o) => o.id === action.payload.optionId);
      if (option) option.priceDelta = action.payload.priceDelta;
    },
    clearMenuPhoto(state) {
      state.photos = [];
      state.coverIndex = 0;
      state.lightboxIndex = null;
    },

    // ─── Menu Draft drawer (Figma 2325:138091) ──────────────────────────────
    /** Store the current form as an unpublished draft ("Save Draft"). */
    saveMenuDraft(state, action: PayloadAction<MenuItem>) {
      const index = state.drafts.findIndex((d) => d.id === action.payload.id);
      if (index >= 0) state.drafts[index] = action.payload;
      else state.drafts.unshift(action.payload);
    },
    removeMenuDraft(state, action: PayloadAction<string>) {
      state.drafts = state.drafts.filter((d) => d.id !== action.payload);
    },

    /**
     * Drop the row a write just returned into the list, ahead of the refresh.
     *
     * `saveMenuItemAction` answers with the saved dish, so the table can show
     * it the moment the request lands instead of a full server round trip
     * later — the gap that made every save read as "did that even work?".
     * `router.refresh()` still runs behind it and reconciles: a row that does
     * not belong on this page under the current filters is taken back out.
     */
    saveMenuItem(state, action: PayloadAction<MenuItem>) {
      const index = state.items.findIndex(
        (item) => item.id === action.payload.id,
      );
      if (index >= 0) {
        state.items[index] = action.payload;
      } else {
        state.items.unshift(action.payload);
        state.total += 1;
      }
    },
  },
});

export const {
  setMenuItems,
  setMenuListQuery,
  setMenuPage,
  setMenuPageSize,
  setMenuSearch,
  removeMenuItem,
  setMenuListPending,
  openMenuOverlay,
  closeMenuOverlay,
  setMenuFilters,
  clearMenuFilters,
  saveMenuItem,
  addMenuPhotos,
  removeMenuPhoto,
  setMenuCoverPhoto,
  setMenuLightboxIndex,
  clearMenuPhoto,
  setModifierGroups,
  addModifierGroup,
  removeModifierGroup,
  setModifierGroupFlag,
  addModifierOption,
  removeModifierOption,
  toggleModifierOption,
  setModifierOptionPrice,
  saveMenuDraft,
  removeMenuDraft,
} = menuSlice.actions;

export default menuSlice.reducer;
